import { useEffect, useState } from "react";
import { Check, Lock, Users } from "lucide-react";

import { fetchFollowers } from "../../Services/SocialService";
import { formatInitials } from "../../utils/format";
import type { CheckInVisibility, SocialUser } from "../../data/types";
import "../../Styles/Presence.css";

/**
 * Vem som ska se att du är här.
 *
 * FOLLOWERS är standard och kräver inget val. SELECTED laddar dina
 * godkända följare och låter dig peka ut några — bara de får se
 * check-in:en och bara de får notisen.
 *
 * Listan är en bekvämlighet, inte en spärr. Servern validerar varje
 * mottagare mot en godkänd följning och kastar resten, så en manipulerad
 * klient kan inte dela sin position bredare än relationen tillåter.
 */
type Props = {
  /** Den inloggades id. Följarlistan hämtas för den, inte för "me". */
  userId: string;
  visibility: CheckInVisibility;
  recipientIds: string[];
  onVisibilityChange: (next: CheckInVisibility) => void;
  onRecipientsChange: (next: string[]) => void;
};

export default function CheckInPrivacy({
  userId,
  visibility,
  recipientIds,
  onVisibilityChange,
  onRecipientsChange,
}: Props) {
  const [followers, setFollowers] = useState<SocialUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visibility !== "SELECTED" || followers.length || isLoading) return;

    setIsLoading(true);
    fetchFollowers(userId)
      .then(setFollowers)
      .catch((e) => {
        console.error("followers fetch failed", e);
        setError("Could not load your followers.");
      })
      .finally(() => setIsLoading(false));
  }, [userId, visibility, followers.length, isLoading]);

  const toggle = (id: string) => {
    onRecipientsChange(
      recipientIds.includes(id)
        ? recipientIds.filter((x) => x !== id)
        : [...recipientIds, id]
    );
  };

  return (
    <div className="privacy" data-testid="check-in-privacy">
      <div className="privacy-options">
        <button
          type="button"
          className={`privacy-option ${visibility === "FOLLOWERS" ? "is-selected" : ""}`}
          aria-pressed={visibility === "FOLLOWERS"}
          onClick={() => onVisibilityChange("FOLLOWERS")}
        >
          <Users size={13} strokeWidth={2.2} />
          Followers
        </button>

        <button
          type="button"
          className={`privacy-option ${visibility === "SELECTED" ? "is-selected" : ""}`}
          aria-pressed={visibility === "SELECTED"}
          onClick={() => onVisibilityChange("SELECTED")}
        >
          <Lock size={13} strokeWidth={2.2} />
          Only selected
        </button>
      </div>

      {visibility === "SELECTED" && (
        <div className="privacy-picker">
          {isLoading && <p className="presence-hint">Loading followers…</p>}
          {error && <p className="presence-error">{error}</p>}

          {!isLoading && !error && followers.length === 0 && (
            <p className="presence-hint">
              No approved followers yet — nobody would see this check-in.
            </p>
          )}

          {followers.map((f) => {
            const picked = recipientIds.includes(f.id);

            return (
              <button
                key={f.id}
                type="button"
                className={`privacy-person ${picked ? "is-picked" : ""}`}
                aria-pressed={picked}
                onClick={() => toggle(f.id)}
              >
                <span className="privacy-person-avatar">
                  {f.avatarUrl ? (
                    <img src={f.avatarUrl} alt="" />
                  ) : (
                    formatInitials(f.displayName)
                  )}
                </span>
                <span className="privacy-person-name">{f.displayName}</span>
                {picked && <Check size={13} strokeWidth={2.6} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
